import { http, HttpResponse, type HttpHandler } from "msw";
import { API_PREFIX, type PaginationMeta } from "@nasam/kernel";
import type { NotificationDto } from "../data";
import { deviceTokenWriteDtoSchema } from "../data";
import { notificationFixtures } from "./notificationFixtures";

const NOTIFICATIONS_PATH = `${API_PREFIX}/notifications`;

export interface NotificationHandlerOptions {
  readonly apiBaseUrl: string;
}

function positiveNumber(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function paginationMeta(
  url: URL,
  page: number,
  limit: number,
  totalCount: number,
): PaginationMeta {
  const totalPages = Math.ceil(totalCount / limit);
  const pageUrl = (targetPage: number): string => {
    const target = new URL(url);
    target.searchParams.set("page", String(targetPage));
    target.searchParams.set("limit", String(limit));
    return target.toString();
  };
  return {
    totalCount,
    currentPage: page,
    totalPages,
    firstPageUrl: pageUrl(1),
    lastPageUrl: pageUrl(Math.max(totalPages, 1)),
    nextPageUrl: page < totalPages ? pageUrl(page + 1) : null,
    previousPageUrl: page > 1 ? pageUrl(page - 1) : null,
  };
}

export function createNotificationHandlers({
  apiBaseUrl,
}: NotificationHandlerOptions): readonly HttpHandler[] {
  let notifications: NotificationDto[] = notificationFixtures.map((item) => ({
    ...item,
  }));
  const registeredTokens = new Set<string>();

  return [
    http.get(`${apiBaseUrl}${NOTIFICATIONS_PATH}`, ({ request }) => {
      const url = new URL(request.url);
      const unreadOnly = url.searchParams.get("unreadOnly") === "true";
      const page = positiveNumber(url.searchParams.get("page"), 1);
      const limit = positiveNumber(url.searchParams.get("limit"), 20);

      const filtered = unreadOnly
        ? notifications.filter((item) => !item.isRead)
        : notifications;
      const sorted = filtered.toSorted(
        (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
      );
      const offset = (page - 1) * limit;

      return HttpResponse.json({
        data: sorted.slice(offset, offset + limit),
        meta: paginationMeta(url, page, limit, sorted.length),
      });
    }),
    http.get(`${apiBaseUrl}${NOTIFICATIONS_PATH}/summary`, () =>
      HttpResponse.json({
        data: {
          unreadCount: notifications.filter((item) => !item.isRead).length,
        },
      }),
    ),
    http.patch(`${apiBaseUrl}${NOTIFICATIONS_PATH}/:id/read`, ({ params }) => {
      const index = notifications.findIndex((item) => item.id === params.id);
      if (index === -1) {
        return HttpResponse.json(
          {
            statusCode: 404,
            path: NOTIFICATIONS_PATH,
            message: "Notification not found",
          },
          { status: 404 },
        );
      }
      const updated: NotificationDto = {
        ...notifications[index]!,
        isRead: true,
      };
      notifications = notifications.with(index, updated);
      return HttpResponse.json({ data: updated });
    }),
    http.post(`${apiBaseUrl}${NOTIFICATIONS_PATH}/read-all`, () => {
      notifications = notifications.map((item) => ({ ...item, isRead: true }));
      return HttpResponse.json({
        message: "All notifications marked as read.",
      });
    }),
    http.post(
      `${apiBaseUrl}${NOTIFICATIONS_PATH}/devices`,
      async ({ request }) => {
        const body = deviceTokenWriteDtoSchema.parse(await request.json());
        registeredTokens.add(body.token);
        return HttpResponse.json(
          { message: "Device registered." },
          { status: 201 },
        );
      },
    ),
    http.delete(
      `${apiBaseUrl}${NOTIFICATIONS_PATH}/devices/:token`,
      ({ params }) => {
        registeredTokens.delete(decodeURIComponent(String(params.token)));
        return HttpResponse.json({ message: "Device unregistered." });
      },
    ),
  ];
}
